import sliderCss from 'blaze-slider/dist/blaze.css?url'

import {createFileRoute, notFound} from '@tanstack/react-router'
import imageUrlBuilder from '@sanity/image-url'
import {useTranslations} from 'use-intl'
import {Suspense} from 'react'
import getAboutPageContent from '~/client/fn/aboutPageContent'
import Breadcrumbs from '~/components/common/Breadcrumbs'
import AboutPageContent from '~/components/about/Content'
import {shopQueryOptions} from '~/client/fn/shop'
import seo from '~/utils/seo'

export const Route = createFileRoute('/$lang/_layout/about')({
    component: RouteComponent,
    loader: async ({context}) => {
        const [pageContent, shopData] = await Promise.all([
            getAboutPageContent({data: {locale: context.locale}}),
            context.queryClient.ensureQueryData(shopQueryOptions()),
        ])

        if (!pageContent || !shopData) {
            throw notFound()
        }

        return {
            pageContent,
            shopData,
        }
    },
    head: ({loaderData}) => {
        if (!loaderData?.pageContent?.seo) {
            return {
                links: [
                    {
                        rel: 'preload',
                        href: sliderCss,
                        as: 'style',
                    },
                    {
                        rel: 'stylesheet',
                        href: sliderCss,
                    },
                ],
            }
        }

        const seoData = loaderData.pageContent.seo
        const builder = imageUrlBuilder({...loaderData.shopData.cms})
        const metaTags = seo({
            title: seoData.title,
            description: seoData.description,
            image: seoData.image
                ? {
                      src: builder.image(seoData.image).width(500).height(250).url(),
                      width: 500,
                      height: 250,
                  }
                : undefined,
        })

        return {
            meta: metaTags.length > 0 ? metaTags : undefined,
            links: [
                {
                    rel: 'preload',
                    href: sliderCss,
                    as: 'style',
                },
                {
                    rel: 'stylesheet',
                    href: sliderCss,
                },
            ],
        }
    },
})

function RouteComponent() {
    const t = useTranslations('about')
    const {pageContent} = Route.useLoaderData()

    return (
        <div className="py-6 lg:py-12 mx-auto max-w-[1920px]">
            <div className="mx-auto max-w-screen-xl px-4 sm:px-6">
                <Breadcrumbs
                    className="mb-2 md:mb-6"
                    items={[
                        {name: t('breadcrumbs.home'), to: '/'},
                        {name: pageContent.title, to: '.'},
                    ]}
                />
                <h1 className="text-2xl font-medium mb-6">{pageContent.title}</h1>
            </div>
            <div className="space-y-8">
                <Suspense>
                    <AboutPageContent elements={pageContent.elements} />
                </Suspense>
            </div>
        </div>
    )
}
